import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../utils/api';

interface OpsStatus {
  ai: { provider: string; model: string | null; postMetadata: boolean };
  verification: { provider: string; simpleAuthEnabled: boolean; imageRetention: string; pendingWithEvidence: number };
  email: { configured: boolean };
  uploads: { configured: boolean };
  errorTracking: { configured: boolean };
  backups: { policy: string; verifiable: boolean };
  deletions30d: Array<{ actor: string; reason: string | null; count: number }>;
  server: { uptimeSec: number; node: string; env: string };
}

const REASON_LABEL: Record<string, string> = {
  not_using: '잘 사용하지 않음',
  missing_features: '필요한 기능 없음',
  privacy: '개인정보 우려',
  other: '기타',
};

/** 운영 상태 — 운영 설정이 코드의 가정과 다르면(가짜 면허조회, 메일 미발송 등) 여기서 바로 보이게 한다 */
const OpsStatusPanel: React.FC = () => {
  const [status, setStatus] = useState<OpsStatus | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    adminAPI
      .getOpsStatus()
      .then((res) => setStatus(res.data))
      .catch((e) => setError(e?.response?.data?.error || '운영 상태를 불러오지 못했습니다.'));
  }, []);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!status) return null;

  const rows: Array<{ label: string; ok: boolean; detail: string }> = [
    {
      label: 'AI 처리',
      ok: status.ai.provider !== 'none',
      detail: status.ai.provider === 'none' ? '설정 없음' : `${status.ai.provider}${status.ai.model ? ` · ${status.ai.model}` : ''}`,
    },
    {
      label: '간편인증 면허조회',
      ok: status.verification.simpleAuthEnabled,
      detail: status.verification.simpleAuthEnabled
        ? `${status.verification.provider} 연결됨`
        : `${status.verification.provider} — 운영에서 꺼 둠(면허증 업로드 → 담당자 검토)`,
    },
    {
      label: '면허 검토 대기',
      ok: status.verification.pendingWithEvidence === 0,
      detail: `${status.verification.pendingWithEvidence}건`,
    },
    { label: '메일 발송', ok: status.email.configured, detail: status.email.configured ? '사용 가능' : '미설정 — 비밀번호 재설정 메일을 보낼 수 없음' },
    { label: '파일 저장', ok: status.uploads.configured, detail: status.uploads.configured ? 'Cloudinary' : '미설정' },
    { label: '오류 수집', ok: status.errorTracking.configured, detail: status.errorTracking.configured ? 'Sentry' : '미설정' },
    { label: 'DB 백업', ok: true, detail: `${status.backups.policy} (앱에서 직접 확인 불가)` },
  ];
  const deletions = status.deletions30d.reduce((n, d) => n + d.count, 0);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5">
      <h3 className="text-lg font-semibold text-gray-900 mb-3">운영 상태</h3>
      <ul className="divide-y divide-gray-100">
        {rows.map((r) => (
          <li key={r.label} className="flex items-start justify-between gap-4 py-2 text-sm">
            <span className="flex items-center gap-2 text-gray-700">
              <span className={`inline-block w-2 h-2 rounded-full ${r.ok ? 'bg-green-500' : 'bg-amber-500'}`} aria-hidden="true" />
              {r.label}
            </span>
            <span className="text-right text-gray-600">{r.detail}</span>
          </li>
        ))}
        <li className="flex items-start justify-between gap-4 py-2 text-sm">
          <span className="text-gray-700 pl-4">최근 30일 탈퇴</span>
          <span className="text-right text-gray-600">
            {deletions === 0
              ? '없음'
              : `${deletions}건 — ` +
                status.deletions30d
                  .map((d) => `${d.actor === 'admin' ? '관리자 처리' : '본인'}·${d.reason ? REASON_LABEL[d.reason] || d.reason : '사유 없음'} ${d.count}`)
                  .join(', ')}
          </span>
        </li>
      </ul>
      <p className="mt-3 text-xs text-gray-400">
        서버 가동 {Math.floor(status.server.uptimeSec / 3600)}시간 · Node {status.server.node} · {status.server.env}
      </p>
    </div>
  );
};

export default OpsStatusPanel;
